import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Landmark, Plus, Loader2 } from 'lucide-react';
import MortgageService from '../../services/MortgageService';
import { Card, Button, toast } from './V2Components';
import MortgageCard from './MortgageCard';
import analytics from '../../services/AnalyticsService';
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';

const MortgageTab = ({ houseId }) => {
    const navigate = useNavigate();
    const [mortgages, setMortgages] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchMortgages = useCallback(async () => {
        try {
            setLoading(true);
            const data = await MortgageService.getMortgages(houseId);
            setMortgages(data.mortgages || []);
        } catch (error) {
            toast.error?.('Failed to load mortgages');
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [houseId]);

    useEffect(() => {
        if (houseId) {
            fetchMortgages();
        }
    }, [houseId, fetchMortgages]);

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
                <div>
                    <h3 className="text-lg font-bold text-[var(--text-main)]">Mortgages</h3>
                    <p className="text-xs text-[var(--text-secondary)] mt-1">
                        Track each loan&apos;s terms and store its monthly statements.
                    </p>
                </div>
                {mortgages.length > 0 && (
                    <Button leadingIcon={Plus} onClick={() => navigate(`/houses/${houseId}/mortgages/new`)}>
                        Add Mortgage
                    </Button>
                )}
            </div>

            {loading ? (
                <div className="flex justify-center p-12">
                    <Loader2 className="animate-spin text-[var(--primary)]" size={32} />
                </div>
            ) : mortgages.length === 0 ? (
                <Card className="p-10 text-center">
                    <Landmark size={48} className="mx-auto mb-3 text-[var(--text-muted)] opacity-50" />
                    <p className="text-[var(--text-muted)] mb-4">No mortgages tracked yet.</p>
                    <Button leadingIcon={Plus} onClick={() => navigate(`/houses/${houseId}/mortgages/new`)}>
                        Add your first mortgage
                    </Button>
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {mortgages.map((m) => (
                        <MortgageCard
                            key={m.id}
                            mortgage={m}
                            onClick={() => {
                                analytics.track(ANALYTICS_EVENTS.HOUSES_DETAILS_MORTGAGE_CLICKED, {});
                                navigate(`/houses/${houseId}/mortgages/${m.id}`);
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default MortgageTab;
